/**
 * Frontend feature flags — public API.
 *
 * Flags here are FE-owned release toggles: they let the frontend ship a change
 * dark and turn it on without a redeploy. They are NOT entitlements — licenses,
 * add-ons and per-company backend behaviour stay in the backend feature-flag
 * system and are read through `selectActualFeatureFlags`.
 *
 * Source of values: PostHog, the only source — see providers/posthogProvider.js
 * and providers.js. On-prem, where PostHog never inits, everything falls back
 * to `false` (browser overrides still work). Nothing outside this folder
 * imports a provider.
 *
 * Usage:
 *   const showNewTable = useFeatureFlag('ic-50123-new-incidents-table')
 *   if (getFeatureFlag('ic-50123-new-incidents-table')) { ... }  // outside React
 */
import { createPosthogProvider } from './providers/posthogProvider'
import { clearOverride, clearOverrides, consumeQueryOverrides, setOverride } from './overrides'
import * as store from './store'

export { useFeatureFlag, useFeatureFlags } from './useFeatureFlag'

let provider = null

/**
 * Starts the active provider. Safe to call once, early (before render).
 *
 * @param {object} [options]
 * @param {import('./providers').FeatureFlagIdentity} [options.identity]
 * @param {import('./providers').FeatureFlagProviderFactory} [options.createProvider]
 */
export const initFeatureFlags = ({ identity, createProvider = createPosthogProvider } = {}) => {
  if (provider) return provider

  consumeQueryOverrides()

  provider = createProvider({
    onFlags: (flags) => store.setProviderFlags(flags, provider?.name),
  })

  provider.start(identity)
  return provider
}

/**
 * Re-evaluates flags for a new identity — call on login and on MSP company
 * switch. No-op before `initFeatureFlags`.
 * @param {import('./providers').FeatureFlagIdentity} identity
 */
export const setFeatureFlagsIdentity = (identity) => provider?.setIdentity(identity)

/**
 * @param {string} key
 * @param {boolean|string|number} [fallback=false]
 */
export const getFeatureFlag = (key, fallback = false) => {
  const flags = store.getFlags()
  return key in flags ? flags[key] : fallback
}

export const getFeatureFlags = () => store.getFlags()

/** False until the provider has reported at least once. */
export const areFeatureFlagsReady = () => store.getSnapshot().ready

export const stopFeatureFlags = () => {
  provider?.stop()
  provider = null
  store.reset()
}

/**
 * Console handle for QA/support: `featureFlags.set('key')`.
 * Overrides are per-browser localStorage values, so this cannot affect anyone else.
 */
if (typeof window !== 'undefined') {
  window.featureFlags = {
    all: () => store.getFlags(),
    get: getFeatureFlag,
    set: (key, value = true) => {
      setOverride(key, value)
      store.refresh()
    },
    unset: (key) => {
      clearOverride(key)
      store.refresh()
    },
    reset: () => {
      clearOverrides()
      store.refresh()
    },
  }
}
